/**
 * CategorySection - Section displaying items of a specific category
 */

import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const CategorySection = ({ category, config, items, onDelete }) => {
  if (!items || items.length === 0) return null;

  // Get category-specific colors
  const getColors = () => {
    switch (category) {
      case 'win':
        return {
          bg: 'bg-emerald-500/10',
          border: 'border-emerald-500/30',
          text: 'text-emerald-400',
          badge: 'bg-emerald-500/20',
        };
      case 'struggle':
        return {
          bg: 'bg-orange-500/10',
          border: 'border-orange-500/30',
          text: 'text-orange-400',
          badge: 'bg-orange-500/20',
        };
      case 'learning':
        return {
          bg: 'bg-blue-500/10',
          border: 'border-blue-500/30',
          text: 'text-blue-400',
          badge: 'bg-blue-500/20',
        };
      case 'grateful':
        return {
          bg: 'bg-purple-500/10',
          border: 'border-purple-500/30',
          text: 'text-purple-400',
          badge: 'bg-purple-500/20',
        };
      default:
        return {
          bg: 'bg-gray-500/10',
          border: 'border-gray-500/30',
          text: 'text-gray-400',
          badge: 'bg-gray-500/20',
        };
    }
  };

  const colors = getColors();

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`${colors.bg} border ${colors.border} rounded-xl p-4`}
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-lg">{config.emoji}</span>
        <span className={`font-medium ${colors.text}`}>{config.label}</span>
        <span className={`text-xs px-2 py-0.5 rounded-full ${colors.badge} ${colors.text}`}>
          {items.length}
        </span>
      </div>

      {/* Items */}
      <div className="space-y-2">
        <AnimatePresence mode="popLayout">
          {items.map((item) => (
            <motion.div
              key={item.id}
              layout
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              className="flex items-start gap-2 group"
            >
              <span className={`text-sm ${colors.text} mt-0.5`}>•</span>
              <div className="flex-1">
                <p className="text-sm text-gray-200">{item.content}</p>
                {item.tag && (
                  <span className="inline-flex items-center gap-1 text-xs text-gray-500 mt-1">
                    {item.tag.emoji && <span>{item.tag.emoji}</span>}
                    <span>{item.tag.name}</span>
                  </span>
                )}
              </div>
              <button
                onClick={() => onDelete(item.id)}
                className="p-1 text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                title="Sil"
              >
                <X className="w-4 h-4" />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default CategorySection;
